import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Link2, Unlink, ShieldCheck, Copy, CheckCircle2,
  Stethoscope,
} from "lucide-react";

/* WhatsApp Settings — technical detail surface hidden behind a dedicated
 * route. This is where admins go when they explicitly need to see the
 * Meta/Gupshup plumbing. The main /app/whatsapp screen intentionally
 * avoids exposing any of this. */

function Row({ label, value, mono, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-[#E7E1D3] last:border-0">
      <div className="text-sm text-ink-mid">{label}</div>
      <div className={`text-sm text-ink text-right ${mono ? "font-mono" : ""}`} data-testid={testid}>{value || "—"}</div>
    </div>
  );
}

export default function WhatsAppSettings() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [disc, setDisc] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/whatsapp/config"); setCfg(data); }
    catch (e) { toast.error(apiError(e, "Failed to load")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const isOwn = cfg?.mode === "clinic_whatsapp";
  const meta = cfg?.meta || {};

  const sync = async () => {
    setSyncing(true);
    try { await api.post("/whatsapp/sync-templates"); toast.success("Templates synced"); load(); }
    catch (e) { toast.error(apiError(e, "Sync failed")); }
    finally { setSyncing(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect your WhatsApp Business account? Automations will fall back to Viyora Managed.")) return;
    setDisc(true);
    try { await api.post("/whatsapp/disconnect"); toast.success("Disconnected"); load(); }
    catch (e) { toast.error(apiError(e, "Disconnect failed")); }
    finally { setDisc(false); }
  };

  if (loading && !cfg) return <div className="min-h-[50vh] grid place-items-center text-ink-soft"><RefreshCw className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-6 py-8 space-y-6" data-testid="wa-settings-page">
      <div>
        <Link to="/app/whatsapp" className="inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-ink" data-testid="wa-settings-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to WhatsApp
        </Link>
        <h1 className="mt-3 font-display text-3xl text-ink">Connection Settings</h1>
        <p className="mt-1 text-sm text-ink-mid">Technical details for administrators.</p>
      </div>

      <div className="rounded-3xl bg-white border border-[#E7E1D3] p-6" data-testid="wa-settings-details">
        <div className="inline-flex items-center gap-2 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Connected
        </div>
        <div className="mt-4 divide-y">
          <Row label="Mode"                 value={isOwn ? "Clinic WhatsApp Business" : "Viyora Managed WhatsApp"} testid="wa-set-mode" />
          <Row label="Verified name"        value={meta.verified_name} testid="wa-set-name" />
          <Row label="Display phone"        value={meta.display_phone} mono testid="wa-set-phone" />
          <Row label="Phone Number ID"      value={meta.phone_number_id_masked} mono testid="wa-set-pnid" />
          <Row label="Business Account ID"  value={meta.waba_id_masked} mono testid="wa-set-waba" />
          <Row label="Access token"         value={meta.access_token_masked} mono testid="wa-set-token" />
          <Row label="Meta verification"    value={<span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="w-3.5 h-3.5" /> Verified</span>} />
          <Row label="Quality rating"       value={meta.quality_rating || "GREEN"} />
          <Row label="Connected at"         value={meta.connected_at} testid="wa-set-connected-at" />
          <Row label="Last synced"          value={meta.last_synced_at || "—"} testid="wa-set-last-sync" />
          <Row label="Provider kind"        value={cfg?.provider_kind} testid="wa-set-provider" />
          <Row label="API version"          value="v19.0" mono />
          <Row label="Approved templates"   value={cfg?.approved_templates_count} testid="wa-set-tpl-count" />
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button onClick={sync} disabled={syncing}
            className="inline-flex items-center gap-2 text-xs font-medium text-ink bg-white border border-[#E7E1D3] hover:border-ink px-4 py-2 rounded-full transition-colors disabled:opacity-50"
            data-testid="wa-set-sync">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> Resync templates
          </button>
          <Link to="/app/whatsapp/connect-meta"
            className="inline-flex items-center gap-2 text-xs font-medium text-white bg-[#D06A42] hover:bg-[#E17550] px-4 py-2 rounded-full transition-colors"
            data-testid="wa-set-connect-meta">
            <Link2 className="w-3.5 h-3.5" /> Connect Meta Cloud API
          </Link>
          <Link to="/app/whatsapp/automations"
            className="inline-flex items-center gap-2 text-xs font-medium text-ink bg-white border border-[#E7E1D3] hover:border-ink px-4 py-2 rounded-full transition-colors"
            data-testid="wa-set-automation-templates">
            <ShieldCheck className="w-3.5 h-3.5" /> Automation templates
          </Link>
          <Link to="/app/whatsapp/diagnostics"
            className="inline-flex items-center gap-2 text-xs font-medium text-ink bg-white border border-[#E7E1D3] hover:border-ink px-4 py-2 rounded-full transition-colors"
            data-testid="wa-set-diagnostics">
            <Stethoscope className="w-3.5 h-3.5" /> Cloud API diagnostics
          </Link>
          {isOwn ? (
            <button onClick={disconnect} disabled={disc}
              className="inline-flex items-center gap-2 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-100 hover:bg-rose-100 px-4 py-2 rounded-full transition-colors disabled:opacity-50"
              data-testid="wa-set-disconnect">
              <Unlink className="w-3.5 h-3.5" /> Disconnect WhatsApp Business
            </button>
          ) : (
            <Link to="/app/whatsapp/connect"
              className="inline-flex items-center gap-2 text-xs font-semibold text-white bg-[#D06A42] hover:bg-[#E17550] px-4 py-2 rounded-full transition-colors"
              data-testid="wa-set-connect">
              <Link2 className="w-3.5 h-3.5" /> Connect your own WhatsApp
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
